import React from 'react';
import useAppSelector from '../../app/redux/useAppSelector';
import { Field } from '../../components/Form';
import LoadingIndicator from '../../components/LoadingIndicator';
import {
  PASSWORD_MIN_LENGTH,
  validatePassword,
} from '../../user/settings/UserAccountSettings';
import useUpdateEmailMutation from '../../user/useUpdateEmailMutation';
import useUpdatePasswordMutation from '../../user/useUpdatePasswordMutation';
import useIsPasswordSet from '../useIsPasswordSet';
import type { OrganizationOnboardingStepBaseProps } from './OrganizationOnboardingStep';
import OrganizationOnboardingStep from './OrganizationOnboardingStep';

type Values = { email: string; password: string };

const OrganizationOnboardingStepAuth = (
  props: OrganizationOnboardingStepBaseProps,
) => {
  const org = useAppSelector((state) => state.session.organization);
  const user = useAppSelector((state) => state.session.user);
  const updateUserPassword = useUpdatePasswordMutation();
  const updateUserEmail = useUpdateEmailMutation();
  const initialValues = { email: user.email ?? '', password: '' };
  const { loading, data: isPasswordSet } = useIsPasswordSet();

  return (
    <OrganizationOnboardingStep<Values>
      {...props}
      initialValues={initialValues}
      title={`Let’s get started, ${user.name}! 🎉`}
      description="Choose a password to log in with"
      validate={(values) =>
        !isPasswordSet ? validatePassword(values.password) : undefined
      }
      onSubmit={async ({ email, password }) => {
        const promises = [];

        if (initialValues.password !== password) {
          promises.push(updateUserPassword({ password }));
        }

        if (initialValues.email !== email) {
          promises.push(updateUserEmail(user.id, email));
        }

        await Promise.all(promises);
      }}
    >
      {loading ? (
        <LoadingIndicator />
      ) : (
        <>
          <Field
            name="email"
            label="Email"
            autoComplete="email"
            {...(org.isVerified
              ? { disabled: true, helpText: 'Email is already verified' }
              : {})}
          />
          {!isPasswordSet && (
            <Field
              name="password"
              label="Password"
              type="password"
              autoComplete="new-password"
              required
              minLength={PASSWORD_MIN_LENGTH}
            />
          )}
        </>
      )}
    </OrganizationOnboardingStep>
  );
};

export default OrganizationOnboardingStepAuth;
